import {
  DashboardPage,
  ClientsPage,
  ContentBlocksPage,
  DisplaysPage,
  RoomsPage,
  RoomTypesPage,
  UserProfilePage,
  VenuesPage,
  SignupPage,
  SigninPage,
  SchedulePage,
  DisplayLinkPage,
  DisplayPreviewPage,
  TvPage,
  DisplayTvsPage,
  AccountSettingsPage,
  SubscriptionPage,
  NotFoundPage,
  ServerErrorPage,
  EventImportPage,
  RunningMessagesPage,
  DisplayLayoutsPage,
  DisplayThemesPage,
  ScreensPage,
  SingleEventsPage,
  RecurringEventsPage,
  PlayListsPage,
  PlayListItemsPage,
  EmbeddingEventTemplatesPage,
  EmbeddingsPage,
  EmbeddingPreviewPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  UserInvitationsPage,
  AcceptInvitationPage,
  UsersPage,
} from "@/pages";
import { DashboardLayout } from "@/components/layouts";
import { USER_ROLES } from "./userRoles";

export interface SiteMapItem {
  name: string;
  subtitle?: string;
  url?: string;
  urlMask?: string;
  component?: React.ComponentType<any>;
  layout?: React.ComponentType<any>;
  visible?: boolean;
  roles?: string[];
  items?: SiteMapItem[];
  options?: any;
  icon?: string; // Icon name for navigation
}

export const componentsMap = {
  DashboardLayout,
  DashboardPage,
  ClientsPage,
  ContentBlocksPage,
  DisplaysPage,
  RoomsPage,
  RoomTypesPage,
  AccountSettingsPage,
  SubscriptionPage,
  UserProfilePage,
  VenuesPage,
  SignupPage,
  SigninPage,
};

const siteMap: SiteMapItem[] = [
  // Private Pages
  {
    name: "Dashboard",
    subtitle: "Your VenueBoards control center",
    url: "/",
    component: DashboardPage,
    visible: true,
    roles: [
      USER_ROLES.ADMIN_ROLE,
      USER_ROLES.MANAGER_ROLE,
      USER_ROLES.CONTENT_MANAGER,
      USER_ROLES.SCHEDULE_MANAGER,
    ],
    items: [],
    options: {},
    icon: "Dashboard",
  },
  {
    name: "Screens",
    subtitle: "View and manage active screens",
    url: "/screens",
    component: ScreensPage,
    visible: true,
    roles: [
      USER_ROLES.ADMIN_ROLE,
      USER_ROLES.MANAGER_ROLE,
      USER_ROLES.CONTENT_MANAGER,
      USER_ROLES.SCHEDULE_MANAGER,
    ],
    items: [],
    options: {},
    icon: "DeviceTv",
  },
  {
    name: "Displays",
    subtitle: "Configure TV displays",
    visible: true,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [
      {
        name: "Display",
        subtitle: "Configure what screens should display",
        url: "/displays",
        component: DisplaysPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "DeviceTv",
      },
      {
        name: "Display Devices",
        subtitle: "Manage devices using the display configuration",
        url: "/displays/:displayId/tvs",
        component: DisplayTvsPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "DeviceTv",
      },
      {
        name: "Preview Display",
        subtitle: "Preview how display looks like",
        url: "/displays/:displayId/preview",
        component: DisplayPreviewPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Eye",
      },
      {
        name: "Themes",
        subtitle: "Manage display themes",
        url: "/themes",
        component: DisplayThemesPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Brush",
      },
      {
        name: "Layouts",
        subtitle: "Manage display layouts",
        url: "/layouts",
        component: DisplayLayoutsPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Layout",
      },
    ],
    options: {},
    icon: "DeviceTv",
  },
  {
    name: "Content",
    subtitle: "Manage display content",
    visible: true,
    roles: [
      USER_ROLES.ADMIN_ROLE,
      USER_ROLES.MANAGER_ROLE,
      USER_ROLES.CONTENT_MANAGER,
    ],
    items: [
      {
        name: "Content Manager",
        subtitle: "Manage content blocks for your screens",
        url: "/content-manager",
        component: ContentBlocksPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.CONTENT_MANAGER,
        ],
        items: [],
        options: {},
        icon: "Blocks",
      },
      {
        name: "Running Messages",
        subtitle: "Manage scrolling messages for your screens",
        url: "/running-messages",
        component: RunningMessagesPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.CONTENT_MANAGER,
        ],
        items: [],
        options: {},
        icon: "Message",
      },
      {
        name: "Play Lists",
        subtitle: "Manage play lists that are displays on your screens",
        url: "/play-lists",
        component: PlayListsPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.CONTENT_MANAGER,
        ],
        items: [],
        options: {},
        icon: "Playlist",
      },
      {
        name: "Play List Items",
        subtitle: "Manage play list items",
        url: "/play-lists/:playListId/items",
        component: PlayListItemsPage,
        visible: false,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.CONTENT_MANAGER,
        ],
        items: [],
        options: {},
        icon: "Playlist",
      },
    ],
    options: {},
    icon: "FileText",
  },
  {
    name: "Events",
    subtitle: "Schedule and manage events",
    visible: true,
    roles: [
      USER_ROLES.ADMIN_ROLE,
      USER_ROLES.MANAGER_ROLE,
      USER_ROLES.SCHEDULE_MANAGER,
    ],
    items: [
      {
        name: "Schedule",
        subtitle: "View event schedule",
        url: "/schedule",
        component: SchedulePage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.SCHEDULE_MANAGER,
        ],
        items: [],
        options: {},
        icon: "Calendar",
      },
      {
        name: "Single Events",
        subtitle: "Manage single events",
        url: "/events/single",
        component: SingleEventsPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.SCHEDULE_MANAGER,
        ],
        items: [],
        options: {},
        icon: "CalendarEvent",
      },
      {
        name: "Recurring Events",
        subtitle: "Manage recurring events",
        url: "/events/recurring",
        component: RecurringEventsPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.SCHEDULE_MANAGER,
        ],
        items: [],
        options: {},
        icon: "CalendarEvent",
      },
      {
        name: "Import Events",
        subtitle: "Import events from ICS files",
        url: "/events/import",
        component: EventImportPage,
        visible: true,
        roles: [
          USER_ROLES.ADMIN_ROLE,
          USER_ROLES.MANAGER_ROLE,
          USER_ROLES.SCHEDULE_MANAGER,
        ],
        items: [],
        options: {},
        icon: "DatabaseImport",
      },
    ],
    options: {},
    icon: "Calendar",
  },
  {
    name: "Embedding",
    subtitle: "",
    visible: true,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [
      {
        name: "Emebeddings",
        subtitle: "Manage embeddings for your websites",
        url: "/embedding/embeddings",
        component: EmbeddingsPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Frame",
      },
      {
        name: "Preview Emebedding",
        subtitle: "",
        url: "/embedding/embeddings/:embeddingId/preview",
        component: EmbeddingPreviewPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Frame",
      },
      {
        name: "Event Templates",
        subtitle: "Configure the way how your events look like on your website",
        url: "/embedding/templates",
        component: EmbeddingEventTemplatesPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Template",
      },
    ],
    options: {},
    icon: "Frame",
  },
  {
    name: "Venues",
    subtitle: "Manage venues and rooms",
    visible: true,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [
      {
        name: "All Venues",
        subtitle: "Manage your venue locations",
        url: "/venues",
        component: VenuesPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Building",
      },
      {
        name: "Room Types",
        subtitle: "Manage room types",
        url: "/room-types",
        component: RoomTypesPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Door",
      },
      {
        name: "Rooms",
        subtitle: "Manage venue rooms",
        url: "/venues/:venueId/rooms",
        component: RoomsPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
        items: [],
        options: {},
        icon: "Door",
      },
    ],
    options: {},
    icon: "Building",
  },
  {
    name: "Clients",
    subtitle: "Manage your clients",
    url: "/clients",
    component: ClientsPage,
    visible: true,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [],
    options: {},
    icon: "Users",
  },
  {
    name: "Link Display",
    subtitle: "",
    url: "/displays/link/:activationCode",
    component: DisplayLinkPage,
    visible: false,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [],
    options: {},
  },
  {
    name: "User Profile",
    subtitle: "Manage your profile information",
    url: "/account/profile",
    component: UserProfilePage,
    visible: false,
    roles: [USER_ROLES.ADMIN_ROLE, USER_ROLES.MANAGER_ROLE],
    items: [],
    options: {},
    icon: "User",
  },
  {
    name: "Account",
    visible: true,
    roles: [USER_ROLES.ADMIN_ROLE],
    items: [
      {
        name: "User Invitations",
        subtitle: "Manage invitations to your account",
        url: "/account/users/invitations",
        component: UserInvitationsPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Users",
        subtitle: "Manage users of your account",
        url: "/account/users",
        component: UsersPage,
        visible: true,
        roles: [USER_ROLES.ADMIN_ROLE],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Account Settings",
        subtitle: "Manage your account settings",
        url: "/account/settings",
        component: AccountSettingsPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE],
        items: [],
        options: {},
        icon: "Settings",
      },
      {
        name: "Subscription",
        subtitle: "Manage your subscription",
        url: "/account/subscription",
        component: SubscriptionPage,
        visible: false,
        roles: [USER_ROLES.ADMIN_ROLE],
        items: [],
        options: {},
        icon: "CreditCard",
      },
    ],
    options: {},
    icon: "User",
  },

  /* **********************************************************
   * Public Pages
   * ********************************************************** */
  {
    name: "Sign Up",
    subtitle: "Create your VenueBoards account",
    url: "/account/signup",
    component: SignupPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Sign In",
    subtitle: "Sign in to your VenueBoards account",
    url: "/account/signin",
    component: SigninPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Forgot Password",
    subtitle: "Request password reset link",
    url: "/account/forgot-password",
    component: ForgotPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Reset Password",
    subtitle: "Reset password",
    url: "/account/reset-password",
    component: ResetPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Accept Invitation",
    subtitle: "Accept invitation to join company at VenueBoards",
    url: "/account/accept-invitation",
    component: AcceptInvitationPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "TV Activation",
    subtitle: "",
    url: "/tv",
    component: TvPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Server Error Page",
    subtitle: "",
    url: "/server-error",
    component: ServerErrorPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  // Not Found
  {
    name: "Page Not Found",
    subtitle: "",
    url: "*",
    component: NotFoundPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
];

export default siteMap;
